import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Helmet } from "react-helmet";
import styled from "styled-components";
import { Link } from "react-router-dom";

import { getAudioPage } from "../configureStore";
import { fetchAudioPage } from "../configureStore/audioPage";
import { getHelmet } from "../utilities";
import { FromTopPage, GridWrapper, Banner, Button } from "../theme";
import NotFound from "../components/NotFound";
import Error from "../components/Error";
import PageLoader from "../components/loader/PageLoader";
import ShowMenu from "../components/menu/ShowMenu";
import AudioPage from "../components/audio/AudioPage";

import Contextual from "../components/Contextual";
import JoinUs from "../components/JoinUs";

const ContextualContent = styled.section`
  margin-top: 90px;

  @media (max-width: 1023px) {
    margin-top: 45px;
  }
`;

const ProgramBanner = styled(Banner)`
  > div > div {
    width: 80%;

    @media (max-width: 1023px) {
      width: 100%;
    }
  }
`;

class Audio extends Component {
  static fetchInitialData({ slug, previewToken }) {
    return [fetchAudioPage(slug, previewToken)];
  }

  componentDidMount() {
    this.fetchContent();
  }

  componentDidUpdate() {
    this.fetchContent();
  }

  fetchContent() {
    const { audioPage, dispatch, match } = this.props;
    if (!audioPage.isFetching && !audioPage.error && !audioPage.isLoaded) {
      dispatch(fetchAudioPage(match.params.slug, match.params.previewToken));
    }
  }

  render() {
    const { error, isLoaded, audio } = this.props.audioPage;

    if (error) {
      if (error === "404") {
        return <NotFound />;
      }

      return <Error message={error} />;
    }

    if (isLoaded) {
      const microdata = {
        "@context": "https://schema.org",
        "@type": "NewsArticle",
        isAccessibleForFree: "http://schema.org/True",
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": `${process.env.RAZZLE_HOSTNAME}${this.props.match.url}`,
        },
        headline: audio.title,
        image: {
          "@type": "ImageObject",
          url: audio.thumb_hero_url,
          width: 1920,
          height: 960,
        },
        datePublished: audio.published_at,
        dateModified: audio.published_at,
        author: audio.authors.map((author) => ({
          "@type": "Person",
          name: author.screenname,
          url: `${process.env.RAZZLE_HOSTNAME}/auteurs/${author.slug}`,
        })),
        publisher: {
          "@type": "Organization",
          name: "Le Média",
          logo: {
            "@type": "ImageObject",
            url: "http://ressources.lemediatv.fr/logo-microdata.png",
            width: 300,
            height: 60,
          },
          url: "https://lemediatv.fr",
        },
        description: audio.headline_or_extract_medium,
        keywords: audio.story_tags.map((tag) => tag.label).join(", "),
        audio: {
          "@type": "AudioObject",
          contentUrl: audio.attachments_by_keys.audio.value,
          description: audio.headline_or_extract_medium,
          name: audio.title,
          thumbnailUrl: audio.thumb_hero_url,
          uploadDate: audio.published_at,
        },
      };

      return (
        <>
          <Helmet>
            {getHelmet(
              audio.title,
              audio.headline_or_extract_medium,
              this.props.match.url,
              audio.thumb_hero_url
            )}

            <script type="application/ld+json">
              {JSON.stringify(microdata)}
            </script>
          </Helmet>
          <ShowMenu
            slug={this.props.match.params.slug}
            category={audio.primary_category}
          />
          <FromTopPage>
            <AudioPage audio={audio} />

            <ProgramBanner>
              <GridWrapper>
                <div>
                  <h1>{audio.primary_category.label}</h1>
                  <p>{audio.primary_category.description}</p>
                </div>
                <Button
                  as={Link}
                  to={`/podcasts/${audio.primary_category.slug}`}
                >
                  En voir +
                </Button>
              </GridWrapper>
            </ProgramBanner>
            <ContextualContent
              as={Contextual}
              content={audio.contextual_stories}
            />
            <JoinUs />
          </FromTopPage>
        </>
      );
    }

    return <PageLoader />;
  }
}

Audio.propTypes = {
  dispatch: PropTypes.func.isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      slug: PropTypes.string.isRequired,
      previewToken: PropTypes.string,
    }).isRequired,
    url: PropTypes.string.isRequired,
  }).isRequired,
  audioPage: PropTypes.shape({
    isFetching: PropTypes.bool.isRequired,
    error: PropTypes.string,
    isLoaded: PropTypes.bool.isRequired,
    audio: PropTypes.shape({
      primary_category: PropTypes.shape({
        label: PropTypes.string.isRequired,
        slug: PropTypes.string.isRequired,
        description: PropTypes.string.isRequired,
      }).isRequired,
      title: PropTypes.string.isRequired,
      attachments_by_keys: PropTypes.shape({
        audio: PropTypes.shape({
          value: PropTypes.string.isRequired,
        }).isRequired,
      }).isRequired,
      published_at: PropTypes.string.isRequired,
      slug: PropTypes.string.isRequired,
      thumb_hero_url: PropTypes.string.isRequired,
      authors: PropTypes.arrayOf(
        PropTypes.shape({
          screenname: PropTypes.string.isRequired,
        }).isRequired
      ).isRequired,
      headline_or_extract_medium: PropTypes.string.isRequired,
      story_tags: PropTypes.arrayOf(
        PropTypes.shape({
          slug: PropTypes.string.isRequired,
          label: PropTypes.string.isRequired,
        })
      ).isRequired,
      contextual_stories: PropTypes.arrayOf(PropTypes.object).isRequired,
    }),
  }).isRequired,
};

export default connect((state, { match }) => ({
  audioPage: getAudioPage(state, match.params.slug),
}))(Audio);
